/* product-badges.js — THE badge resolver. One ordered vocabulary, one set of
 * rules, consumed by every renderer that paints a product badge.
 *
 * WHY THIS FILE EXISTS
 * Badges used to be authored three times: once in snippets/product-card.liquid,
 * once in the vanilla grid (assets/typesense-plp.js) and once in the React
 * island (src/islands/plp/Tile.jsx). They drifted, twice:
 *   - 2026-09-08 the store-wide "Sold out" disable landed in the Liquid card
 *     and in typesense-plp.js but NOT in Tile.jsx, so island tiles kept
 *     shouting Sold out at purchasable products. That defect was still live on
 *     main when this file was written (Tile.jsx:146).
 *   - the "New" badge only ever existed in Liquid, so a product less than 30
 *     days old was New on the SSR grid and unbadged the moment Typesense took
 *     the grid over — the same product, two answers, on the same scroll.
 * Both JS renderers now call window.STBadges.resolve(). Liquid cannot call JS,
 * so snippets/product-badges.liquid is a DOCUMENTED MIRROR of the table below
 * and scripts/badge-parity.mjs fails CI when the two disagree.
 *
 * The resolver returns DATA, never markup: [{ key, label, cls }]. Markup is one
 * <span class="card__badge …"> per entry and lives in each renderer, because
 * each renderer has its own escaping rules. The parity script asserts that no
 * renderer authors a badge LABEL of its own.
 */
(function () {
  'use strict';

  /* ---- rank scale -----------------------------------------------------------
   * Higher wins. The scale is deliberately sparse so a staff-set curated
   * priority can be slotted anywhere against the computed badges without
   * renumbering:
   *
   *   100  availability STATE — how you can buy it at all. Never suppressed:
   *        hiding "Call for price" to make room for "Save" would quote a
   *        saving on a product whose price we refuse to show.
   *    90  Configurable — also state (which purchase path this product uses).
   *   0-99 CURATED (staff `priority`, default 50). Free to outrank Save and
   *        New, and — at 91+ — even Configurable, which is the point: a
   *        "CLEARANCE" or "RUN OUT" chip is the whole reason the product is
   *        being merchandised, and staff must be able to say so.
   *    40  Save / % Off — computed, commercially important, but only ~3% of
   *        active products carry a compare_at_price today (7 of 250 sampled
   *        2026-09-10), so it is rarely the badge competing for the slot.
   *    20  New — nice to have, first to be dropped.
   */
  var RANK = { cfp: 100, store: 100, out: 100, cfg: 90, curated: 50, save: 40, new: 20 };

  /* Per-surface caps. A tile is 257px wide at a 390px viewport (66vw) and a
   * single uppercase chip such as CONFIGURABLE measures ~95px, so two chips is
   * what actually fits on one line on a phone — three overflow the tile.
   * The PDP buy box is 358px in the same viewport and the chips sit on their
   * own row above the price, so it can carry one more. */
  var MAX = { card: 2, pdp: 3 };

  /* Save badge: `auto` shows the percentage when the discount is deep enough to
   * read as a sale event rather than a trim, and dollars otherwise. 30% is the
   * threshold because that is the number in the reference Luke gave (the
   * Woolworths circular "30% Off"), and because below it the dollar figure is
   * almost always the bigger, more concrete number on tapware — a 15% cut on a
   * $260 mixer is "Save $39", which lands harder than "15% Off". Staff can
   * force either form from theme settings. */
  var SAVE = { style: 'auto', percentThreshold: 30, minCents: 100 };

  /* THE SAVING FLOOR. A compare-at less than $1.00 above the price is not a
   * discount, it is price-rule rounding, and no surface may advertise it.
   * Turner Hastings' staff reported it from the live site on 2026-09-18: the
   * Narva back-to-wall suite at $897.00 carried "SAVE $0.30" against a
   * compare-at of $897.30, and a Novi butler sink read "$1,528.00 ~~$1,528.20~~".
   * 46 variants in that one brand sat 10-50 cents under their compare-at,
   * all of them the RRP x factor rule landing a few cents short of the RRP.
   * The data is being corrected separately; this rule is what stops the NEXT
   * rounding artefact reaching a shopper.
   *
   * isSaving() is the single answer to "is there a saving worth showing", for
   * the badge AND for the struck-through RRP beside the price — the strike
   * without the badge is the same false claim in a quieter voice. The gap is
   * rounded to whole cents before the comparison so a genuine $1.00 saving on
   * dollar floats (10.10 - 9.10 = 0.9999999999999991) is not lost to binary
   * arithmetic. `unit` is 'dollars' (the Typesense index, card-json) or
   * 'cents' (Shopify's product and variant JSON, which ui.js and the sticky
   * bar read). snippets/product-badges.liquid mirrors minCents as
   * save_min_cents; scripts/badge-parity.mjs holds the two equal. */
  function isSaving(price, compareAt, unit) {
    var p = Number(price);
    var c = Number(compareAt);
    if (!(c > p) || isNaN(p) || p < 0) return false;
    var gapCents = unit === 'cents' ? Math.round(c - p) : Math.round((c - p) * 100);
    return gapCents >= SAVE.minCents;
  }

  var CLASS = 'card__badge';
  var STYLES = ['ink', 'accent', 'sale', 'outline', 'muted'];

  /* TEMP(2026-09-08, carried forward from the three old copies): Sold out is
   * disabled STORE-WIDE. Pre-launch inventory quantities are placeholder
   * zeros, so the badge fires on products that are perfectly purchasable.
   * Restore by flipping this to true — in THIS file only. That single switch
   * is the whole reason the resolver exists: the previous three-copy
   * arrangement let the island keep rendering the badge for two days after
   * the other two renderers had dropped it. */
  var SOLD_OUT_ENABLED = false;

  function clamp(n, lo, hi) { n = Number(n); if (isNaN(n)) return lo; return Math.max(lo, Math.min(hi, n)); }

  /* A curated entry is live when it is enabled and today sits inside its
   * window. Both dates are optional and independent: starts_at with no
   * ends_at runs forever from that date, ends_at with no starts_at runs until
   * it. Dates are compared as whole days in the shop's own clock — an ends_at
   * of 2026-12-25 includes all of Christmas Day rather than expiring at
   * midnight UTC, which for an Australian store is mid-afternoon on the day
   * the promotion is supposed to be running. */
  function curatedLive(entry, todayISO) {
    if (!entry || entry.enabled === false) return false;
    if (!entry.label) return false;
    if (entry.starts_at && String(entry.starts_at).slice(0, 10) > todayISO) return false;
    if (entry.ends_at && String(entry.ends_at).slice(0, 10) < todayISO) return false;
    return true;
  }

  /* ctx (renderer-neutral — every field optional):
   *   availability  'online' | 'call_for_price' | 'in_store_only' | 'component'
   *   tags          string[]        (Shopify tags; only 'configurable' is read)
   *   price, compare_at_price       numbers in the SAME unit — dollars unless
   *                                 opts.unit is 'cents' (the $1 saving floor
   *                                 needs to know which)
   *   price_varies  bool            the product's variants are not all one price
   *   age_days      integer days since creation
   *   available     bool            (inventory truth, for the disabled Sold out)
   *   b2b           bool            (a trade session: index prices are RETAIL)
   *   curated       [{label, style, priority, starts_at, ends_at, enabled}]
   *   today         'YYYY-MM-DD'
   * opts:
   *   surface 'card'|'pdp', max, saveStyle, percentThreshold, money(fn),
   *   unit 'dollars' (default) | 'cents'
   */
  function resolve(ctx, opts) {
    ctx = ctx || {}; opts = opts || {};
    var out = [];
    var avail = ctx.availability || 'online';
    // a component is a configurator part and never renders as a product at all;
    // the callers already drop it, this is the belt.
    if (avail === 'component') return out;

    var isCfg = (ctx.tags || []).indexOf('configurable') > -1;
    var seq = 0;
    function push(key, label, cls, rank) { out.push({ key: key, label: label, cls: cls, rank: rank, seq: seq++ }); }

    /* --- ORDER OF DECLARATION IS THE TIE-BREAK. Mirrored, in this order, by
     *     snippets/product-badges.liquid. Do not reorder one without the
     *     other; scripts/badge-parity.mjs enforces it. --- */

    if (isCfg) push('cfg', 'Configurable', CLASS + ' ' + CLASS + '--cfg', RANK.cfg);
    if (avail === 'call_for_price') push('cfp', 'Call for price', CLASS + ' ' + CLASS + '--cfp', RANK.cfp);
    if (avail === 'in_store_only') push('store', 'In-store only', CLASS + ' ' + CLASS + '--store', RANK.store);

    // curated, staff-authored (product_badge metaobject -> custom.badges)
    var today = ctx.today || new Date().toISOString().slice(0, 10);
    (ctx.curated || []).forEach(function (c) {
      if (!curatedLive(c, today)) return;
      var cstyle = String(c.style || 'ink').toLowerCase();
      if (STYLES.indexOf(cstyle) < 0) cstyle = 'ink';
      push('curated', String(c.label),
        CLASS + ' ' + CLASS + '--curated ' + CLASS + '--' + cstyle,
        clamp(c.priority == null ? RANK.curated : c.priority, 0, 99));
    });

    /* Save. Suppressions, all deliberate:
     *  - call_for_price: quoting a saving would quote the price the surface is
     *    explicitly refusing to show (this guard predates the resolver and the
     *    reason is unchanged).
     *  - b2b ON A TILE ONLY: tile and index prices in a trade session are
     *    RETAIL, not the shopper's catalog price, so a retail markdown is not
     *    their saving. The PDP is different and must not inherit this — there
     *    Liquid renders the customer's real catalog price, so the saving is
     *    genuine and has always been shown, worded "You save". Suppressing it
     *    on both surfaces would have been a silent regression for trade
     *    customers.
     *  - price_varies ON A TILE ONLY: a tile whose variants differ in price
     *    reads "From $1,420" (the cheapest), and `compare_at_price` is that
     *    same cheapest variant's compare-at. "Save $180" beside a FROM price
     *    attaches one variant's discount to a whole range — the shopper cannot
     *    tell which of 28 vanities saves them $180, and the 27 others do not.
     *    The PDP keeps the badge because the PDP prices ONE variant and ui.js
     *    repaints the chip on every variant change, so there the saving is
     *    always the saving on the thing being looked at.
     *  - a gap under $1.00, on EVERY surface: see isSaving() above. */
    var cap = Number(ctx.compare_at_price) || 0;
    var price = Number(ctx.price) || 0;
    var surface = opts.surface || 'card';
    var hideSave = avail === 'call_for_price'
      || (ctx.b2b && surface === 'card')
      || (ctx.price_varies && surface === 'card');
    if (!hideSave && isSaving(price, cap, opts.unit)) {
      var pct = Math.round(((cap - price) / cap) * 100);
      var sstyle = opts.saveStyle || SAVE.style;
      var thresh = opts.percentThreshold == null ? SAVE.percentThreshold : Number(opts.percentThreshold);
      var usePct = sstyle === 'percent' || (sstyle === 'auto' && pct >= thresh);
      var money = opts.money || function (n) { return '$' + n; };
      var prefix = ctx.b2b ? 'You save ' : 'Save ';
      push('save', usePct ? pct + '% Off' : prefix + money(cap - price), CLASS, RANK.save);
    }

    if (Number(ctx.age_days) >= 0 && Number(ctx.age_days) < 30) push('new', 'New', CLASS + ' ' + CLASS + '--new', RANK.new);

    // Sold out — see SOLD_OUT_ENABLED at the top of this file.
    if (SOLD_OUT_ENABLED && ctx.available === false && !isCfg) {
      push('out', 'Sold out', CLASS + ' ' + CLASS + '--out', RANK.out);
    }

    out.sort(function (a, b) { return b.rank - a.rank || a.seq - b.seq; });
    var max = opts.max || MAX[opts.surface || 'card'] || MAX.card;
    return out.slice(0, max).map(function (b) { return { key: b.key, label: b.label, cls: b.cls }; });
  }

  /* Curated vocabulary published by snippets/product-badge-map.liquid, keyed by
   * metaobject handle. Client renderers get a product's curated badges as a
   * list of HANDLES on the search hit (hit.badges) and join them here — the
   * same shape as window.ST_SWATCH. */
  function fromHandles(handles) {
    var vocab = (window.ST_BADGE_VOCAB) || {};
    return (handles || []).map(function (h) { return vocab[h]; }).filter(Boolean);
  }

  window.STBadges = {
    resolve: resolve,
    isSaving: isSaving,
    fromHandles: fromHandles,
    RANK: RANK,
    MAX: MAX,
    SAVE: SAVE,
    STYLES: STYLES,
    CLASS: CLASS,
    SOLD_OUT_ENABLED: SOLD_OUT_ENABLED,
    // settings are published by the Liquid layer (theme settings are not
    // readable from a static asset); renderers pass them through to resolve()
    opts: function (surface) {
      var s = window.ST_BADGE_SETTINGS || {};
      return {
        surface: surface,
        max: s['max_' + surface],
        saveStyle: s.save_style,
        percentThreshold: s.percent_threshold,
      };
    },
  };
}());
